import React, {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAppSelector } from '../../app/hooks';
import { selectUserProfile } from '../../selectors/user';
import {
  getLayoutPreference,
  removeLayoutPreference,
  resolvePaneUserKey,
  saveLayoutPreference,
} from '../../utils/local-storage';
import LayoutPreferenceDialog, {
  DEFAULT_PANE_SIZES,
} from './LayoutPreferenceDialog';

/**
 * Hosts the "Layout preference" dialog for the whole app (App subtree), so the
 * header ProfileMenu can open it from anywhere — including the task-router lobby,
 * where the 3-pane console (and its PanelGroup) isn't mounted yet.
 *
 * Apply/Reset always persist the agent's explicit split to local storage; when
 * the 3-pane console IS mounted, MainPageLayout registers a pane setter so the
 * same action also resizes the live panes. Live sizes are read through a ref and
 * snapshotted only when the dialog opens, so dragging the panes never re-renders
 * this provider (which wraps the entire app).
 */
interface LayoutPreferenceContextValue {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  /** MainPageLayout reports the live pane sizes here on every layout change. */
  reportLiveSizes: (sizes: number[] | null) => void;
  /**
   * MainPageLayout registers its imperative resize fn on mount and clears it
   * (null) on unmount, so Apply/Reset drive the live panes only while they exist.
   */
  registerPaneSetter: (setter: ((sizes: number[]) => void) | null) => void;
}

const LayoutPreferenceContext = createContext<LayoutPreferenceContextValue>({
  isOpen: false,
  open: () => {},
  close: () => {},
  reportLiveSizes: () => {},
  registerPaneSetter: () => {},
});

export const LayoutPreferenceProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const userProfile = useAppSelector(selectUserProfile);
  const userKey = resolvePaneUserKey(userProfile);

  const [isOpen, setIsOpen] = useState(false);
  // Sizes the dialog shows: snapshotted from the live panes / saved preference
  // when it opens (see `open`), never on every drag.
  const [dialogSizes, setDialogSizes] = useState<number[]>(DEFAULT_PANE_SIZES);
  // Ref (not state) so per-frame drag reports don't re-render the whole app.
  const liveSizesRef = useRef<number[] | null>(null);
  const paneSetterRef = useRef<((sizes: number[]) => void) | null>(null);

  const open = useCallback(() => {
    setDialogSizes(
      liveSizesRef.current ?? getLayoutPreference(userKey) ?? DEFAULT_PANE_SIZES
    );
    setIsOpen(true);
  }, [userKey]);

  const close = useCallback(() => setIsOpen(false), []);

  const reportLiveSizes = useCallback((sizes: number[] | null) => {
    liveSizesRef.current = sizes;
  }, []);

  const registerPaneSetter = useCallback(
    (setter: ((sizes: number[]) => void) | null) => {
      paneSetterRef.current = setter;
      // Panes gone (agent returned to the lobby): forget their sizes so the
      // dialog falls back to the saved preference / default next time it opens.
      if (!setter) liveSizesRef.current = null;
    },
    []
  );

  const handleApply = useCallback(
    (sizes: [number, number, number]) => {
      saveLayoutPreference(userKey, sizes);
      paneSetterRef.current?.(sizes);
    },
    [userKey]
  );

  const handleReset = useCallback(() => {
    removeLayoutPreference(userKey);
    paneSetterRef.current?.(DEFAULT_PANE_SIZES);
  }, [userKey]);

  const value = useMemo(
    () => ({ isOpen, open, close, reportLiveSizes, registerPaneSetter }),
    [isOpen, open, close, reportLiveSizes, registerPaneSetter]
  );

  return (
    <LayoutPreferenceContext.Provider value={value}>
      {children}
      <LayoutPreferenceDialog
        open={isOpen}
        onClose={close}
        sizes={dialogSizes}
        onApply={handleApply}
        onReset={handleReset}
      />
    </LayoutPreferenceContext.Provider>
  );
};

export const useLayoutPreference = () => useContext(LayoutPreferenceContext);
